import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';
import {
  recommendationRules,
  recommendationPriorities,
} from '@packages/domains/recommendations/data-types';

const toolName = 'create-site-recommendations' as const;

export const recommendationPrioritySchema = z.enum([
  ...recommendationPriorities,
]);

export const recommendationRuleSchema = z.enum([...recommendationRules]);

export const recommendationItemSchema = z.object({
  elementId: z.string().optional().describe('The element ID in the DOM'),
  assetId: z
    .string()
    .optional()
    .describe('The asset ID for asset-based recommendations'),
  assetUrl: z.string().optional().describe('The asset URL'),
  pageId: z
    .string()
    .optional()
    .describe('The page ID for page-based recommendations'),
  collectionId: z
    .string()
    .optional()
    .describe('The CMS collection ID for collection-based recommendations'),
  itemId: z.string().optional().describe('The CMS item ID'),
  fieldSlug: z.string().optional().describe('The CMS field slug'),
  fileId: z.string().optional().describe('The CMS file ID of the image'),
  rule: recommendationRuleSchema.describe(
    'The recommendation rule that identifies the type of issue'
  ),
  proposal: z
    .record(z.unknown())
    .optional()
    .describe(
      'The proposed fix as a key-value object (e.g. {altText: "..."} or {seoTitle: "..."})'
    ),
  evidence: z
    .string()
    .optional()
    .describe(
      'Evidence of the issue (e.g. a broken URL, a missing field value)'
    ),
  errorMessage: z
    .string()
    .optional()
    .describe('Error message if generation failed'),
  priority: recommendationPrioritySchema.optional(),
  localeId: z
    .string()
    .optional()
    .describe('The locale ID for locale-scoped recommendations'),
});

export type RecommendationItem = z.infer<typeof recommendationItemSchema>;

export const createSiteRecommendationsArgsSchema = z.object({
  siteId: z.string().describe('The site ID to create recommendations for'),
  sourceId: z
    .string()
    .describe('The source ID (e.g., audit job ID or tool invocation ID)'),
  sourceType: z
    .enum(['job', 'tool', 'user'])
    .describe('The type of source creating these recommendations'),
  items: z
    .array(recommendationItemSchema)
    .describe('Array of recommendation items to create'),
  preserveStatusOnUpdate: z
    .boolean()
    .optional()
    .describe(
      'When true, existing recommendations keep their current status on update. ' +
        'New recommendations are always created with pending status.'
    ),
  omitResults: z
    .boolean()
    .optional()
    .describe(
      'Internal optimization for high-volume audit batches. When true, ' +
        'skips the follow-up read that hydrates `recommendationIds`. ' +
        '`createdCount` and `updatedCount` remain accurate; ' +
        '`recommendationIds` will be empty. Callers that need the IDs ' +
        '(e.g. to flip status afterwards) must leave this false / unset.'
    ),
});

export type CreateSiteRecommendationsArgs = z.infer<
  typeof createSiteRecommendationsArgsSchema
>;

export const createSiteRecommendationsResultSchema = z.object({
  createdCount: z.number(),
  updatedCount: z.number(),
  recommendationIds: z.array(z.string()),
  errors: z.array(z.string()),
});

export type CreateSiteRecommendationsResult = z.infer<
  typeof createSiteRecommendationsResultSchema
>;

export const createSiteRecommendationsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create site recommendations',
  family: 'recommendations',
  prettyStatus: {
    executed: 'Created site recommendations',
    executing: 'Creating site recommendations',
    failed: 'Failed to create site recommendations',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Creates site recommendations based on audit results. This tool stores
      recommendations in the database for later review and action by users.

      Topic, type, and default priority are automatically derived from the
      recommendation rule. Priority can optionally be overridden per item.

      Each recommendation can be associated with pages, assets, CMS collections,
      or elements, and includes a proposal (suggested fix).
    `,
    inputSchema: createSiteRecommendationsArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteAeoRecommendations',
      action: 'manage',
      resolveResources: (args) => [
        {type: 'SiteAeoRecommendations', id: args.siteId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 60000,
});
